import React, { Component, PropTypes } from 'react';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import ModalDialog from 'common/components/modal/modal_dialog';
import { MoneyInputField, TextAreaField, CheckBoxField, DEFAULT_REQUIRED_MESSAGE }
       from 'common/components/form_fields';
import AnimatedButton from 'common/components/animated_button';
import Money from 'common/components/money';

/**
 * Modal used to negotiate a value (e.g. on 1-1 projects and entry offers).
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

let Form = (props) => {
  const { error, handleSubmit, submitting, submitFailed, onClose, action, currentValue,
    currentFee, currentTotal, fee, total} = props;

  return (
    <form onSubmit={handleSubmit}>

      {action.intro && action.intro(props)}

      {action.requiresNewValue &&
      <fieldset>
        <div className="row">
          <div className="columns medium-8">
            <Field name="next_value" label="Amount" component={MoneyInputField} />

            {action.showFee && fee > 0 && <div>+ <Money value={fee}/> crowdspring fee</div>}

            {action.showFee && <div className="total">Total: <Money value={total}/></div>}
          </div>
        </div>
      </fieldset>
      }

      {action.showValue && !!currentValue &&
      <fieldset>
        <div className="form-group">
          <label className="money-label">Amount</label>
          <Money value={currentValue} className="amount"/>

          {action.showCurrentFee && currentFee > 0 &&
          <div>+ <Money value={currentFee}/> crowdspring fee</div>}

          {action.showCurrentFee &&
          <div className="total">Total: <Money value={currentTotal}/></div>}
        </div>
      </fieldset>}

      {action.allowsComment &&
      <fieldset>
        <Field name="comment" description="Optional Comment" component={TextAreaField} />
      </fieldset>}

      {action.disclaimer &&
      <fieldset>
        <Field name="accepted_disclaimer" component={CheckBoxField} label={action.disclaimer}/>
      </fieldset>}

      <div className="modal-actions">
        <AnimatedButton processing={submitting} type="submit" label={action.label}
          processingLabel="Please Wait..." errorMsg={error || submitFailed}
          successMsg="Done!"/>

        <button className="btn btn-text btn-block" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>)
}

const validate = (values, {action: {requiresNewValue, disclaimer}}) => {
  const errors = {}

  /* validate fields according to action definition */
  if (requiresNewValue && !values.next_value) {
    errors.next_value = DEFAULT_REQUIRED_MESSAGE
  }
  if (disclaimer && !values.accepted_disclaimer) {
    errors.accepted_disclaimer = "You must confirm that you understand"
  }

  return errors
}

Form = reduxForm({
  form: "NegotiationModal",
  validate,
})(Form);

export default class NegotiationModal extends Component {
  constructor(props) {
    super(props);

    this.state = {done: false};

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onClose = this.onClose.bind(this);
  }

  handleSubmit(formData) {
    const { selectedCreative, entry, action, advanceRequest, onDone } = this.props;
    let data = { ...formData, next_status: action.nextStatus };

    /* add selected creative to request data if required */
    if (action.requiresNewCreative) {
      data['next_creative'] = selectedCreative.id;
    }

    /* add related entry to request data if present */
    if (entry) {
      data['entry'] = entry.id;
    }

    return advanceRequest(data)
      .then((res) => {
        this.setState({ done: true })
        /* invoke callback if present */
        if (onDone) {
          onDone(res.data);
        }
      })
      .catch((res) => {
        const data = res.data;
        let globalError = 'Unknown error. Please try again.';

        if (data) {
          for (const field of Object.keys(data)) {
            /* global error */
            if (field === 'detail') {
              globalError = data[field];

            /* global error related to next status */
            } else if (field == 'next_status') {
              globalError = 'This action is not valid at the moment'
            /* field-specific errors */
            } else if (data[field]) {
              throw new SubmissionError({ [field]: data[field][0] })
            };
          }
        }
        throw new SubmissionError({ _error: globalError });
      });
  }

  onClose() {
    this.setState({done: false});
    this.props.onClose();
  }

  render() {
    const { action } = this.props;

    if (!action) {
      return null;
    }

    return (
      <ModalDialog
        modalOpen={true}
        dialogClasses="negotiation-modal"
        closeAction={this.onClose}
        modalHeader={action.label}
      >
        {this.state.done?
        <div className="row columns done">
          <h3 className="text-success">
            <i className={"fa fa-" + (action.isNegative? "times-circle":"check-circle")}/><br/>
            {action.doneTitle}
          </h3>

          {!action.isNegative && <h4>What happens next?</h4>}

          <p>{action.doneDetails}</p>

          <div className="modal-actions">
            <button className="btn btn-primary" onClick={this.onClose}>Done</button>
          </div>
        </div>:
        <div className="row columns small-16">
          {action.confirmation && <h3 className="confirmation-title">{action.confirmation}</h3>}

          <Form {...this.props} onSubmit={this.handleSubmit} onClose={this.onClose} />
        </div>}
      </ModalDialog>
    )
  }
}

/**
 * PropTypes.
 *
 * @property {object} selectedCreative - selected creative, if any
 * @property {object} entry - related entry, if any
 * @property {object} action - negotiation action being performed
 * @property {number} fee - next negotiation fee
 * @property {number} total - next negotiation total
 * @property {number} currentValue - current negotiation value
 * @property {number} currentFee - current negotiation fee
 * @property {number} currentTotal - current negotiation total
 * @property {func} advanceRequest - request to advance negotiation
 * @property {func} onClose - close modal
 * @property {func} onDone - callback called when negotiation is successfully advanced
 */
NegotiationModal.propTypes = {
  selectedCreative: PropTypes.object,
  entry: PropTypes.object,
  action: PropTypes.object,
  fee: PropTypes.number,
  total: PropTypes.number,
  currentValue: PropTypes.number,
  currentFee: PropTypes.number,
  currentTotal: PropTypes.number,
  advanceRequest: PropTypes.func,
  onClose: PropTypes.func,
  onDone: PropTypes.func,
};



// WEBPACK FOOTER //
// ./common/components/modal/negotiation.jsx